import React, { useState, useEffect, useRef, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { base44 } from "api/apiClient";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import Layout from "../components/Layout";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "components/ui/card";
import { useNavigate } from "react-router-dom";
import { createPageUrl } from "utils";
import { Alert, AlertDescription } from "components/ui/alert";
import { Label } from "components/ui/label";
import { Input } from "components/ui/input";
import { Checkbox } from "components/ui/checkbox";
import { DEFAULT_MENU_CATEGORIES, normalizeMenuCategories } from "utils";
import POSIntegrationsSection from "../components/POSIntegrationsSection";
import { Plus, Settings, Sparkles, AlertTriangle, Plug, Upload, X, CheckCircle, Trash2 } from "lucide-react";

export default function Setup() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { fetchUser } = useAuth();
  const [showRestaurantForm, setShowRestaurantForm] = useState(false);
  const [saveError, setSaveError] = useState(null);

  // Orderbird file import
  const fileInputRef = useRef(null);
  const [importUploading, setImportUploading] = useState(false);
  const [importResult, setImportResult] = useState(null); // {ok, message}

  const handleOrderbirdUpload = async (file) => {
    setImportUploading(true);
    setImportResult(null);
    try {
      // Ensure Orderbird integration exists (auto-create if not)
      const integrations = await base44.pos.getIntegrations();
      let integration = integrations.find((i) => i.provider === 'orderbird');
      if (!integration) {
        integration = await base44.pos.createIntegration('orderbird');
      }
      const log = await base44.pos.uploadOrderbirdCSV(integration.id, file);
      if (log.status === 'success') {
        setImportResult({ ok: true, message: t('pos.orderbirdUploadSuccess', { count: log.records_synced }) });
        queryClient.invalidateQueries({ queryKey: ['sales'] });
      } else {
        setImportResult({ ok: false, message: t('pos.orderbirdUploadError', { msg: log.error_message || 'Unknown error' }) });
      }
    } catch (err) {
      setImportResult({ ok: false, message: t('pos.orderbirdUploadError', { msg: err?.response?.data?.error || err.message || 'Unknown error' }) });
    } finally {
      setImportUploading(false);
    }
  };

  // Days of week in current language — memoized to avoid infinite re-render in useEffect
  const daysOfWeek = useMemo(() => [
    t("setup.monday"),
    t("setup.tuesday"),
    t("setup.wednesday"),
    t("setup.thursday"),
    t("setup.friday"),
    t("setup.saturday"),
    t("setup.sunday")
  ], [t]);
  
  const [restaurantData, setRestaurantData] = useState({
    name: '',
    opening_days: [daysOfWeek[1], daysOfWeek[2], daysOfWeek[3], daysOfWeek[4], daysOfWeek[5], daysOfWeek[6]],
    days_per_week: 6,
    lunch_service: true,
    dinner_service: true,
    lunch_start: '11:30',
    lunch_end: '14:30',
    dinner_start: '17:30',
    dinner_end: '22:00',
    menu_categories: DEFAULT_MENU_CATEGORIES,
  });

  // Prüfe ob schon Daten vorhanden sind
  const { data: sales } = useQuery({
    queryKey: ['sales'],
    queryFn: () => base44.entities.Sale.list(),
    initialData: [],
  });

  const { data: inventory } = useQuery({
    queryKey: ['inventory'],
    queryFn: () => base44.entities.InventoryItem.list(),
    initialData: [],
  });

  const { data: recipes } = useQuery({
    queryKey: ['recipes'],
    queryFn: () => base44.entities.Recipe.list(),
    initialData: [],
  });

  const { data: tasks } = useQuery({
    queryKey: ['tasks'],
    queryFn: () => base44.entities.Task.list(),
    initialData: [],
  });


  const { data: restaurant } = useQuery({
    queryKey: ['restaurant'],
    queryFn: async () => {
      const data = await base44.entities.Restaurant.list();
      return data[0];
    },
  });

  const hasExistingData = sales.length > 0 || inventory.length > 0 || recipes.length > 0 || tasks.length > 0;

  // Update restaurantData wenn Restaurant geladen ist
  useEffect(() => {
    if (restaurant && !showRestaurantForm) {
      setRestaurantData({
        name: restaurant.name || '',
        opening_days: restaurant.opening_days || [daysOfWeek[1], daysOfWeek[2], daysOfWeek[3], daysOfWeek[4], daysOfWeek[5], daysOfWeek[6]],
        days_per_week: restaurant.days_per_week || 6,
        lunch_service: restaurant.lunch_service !== undefined ? restaurant.lunch_service : true,
        dinner_service: restaurant.dinner_service !== undefined ? restaurant.dinner_service : true,
        lunch_start: restaurant.lunch_start || '11:30',
        lunch_end: restaurant.lunch_end || '14:30',
        dinner_start: restaurant.dinner_start || '17:30',
        dinner_end: restaurant.dinner_end || '22:00',
        menu_categories: normalizeMenuCategories(restaurant.menu_categories).length > 0
          ? normalizeMenuCategories(restaurant.menu_categories)
          : DEFAULT_MENU_CATEGORIES,
      });
    }
  }, [restaurant, showRestaurantForm, daysOfWeek]);

  const saveRestaurantSettings = async () => {
    setSaveError(null);
    try {
      const menuCategories = normalizeMenuCategories(restaurantData.menu_categories);
      if (menuCategories.length === 0) {
        setSaveError(t('setup.menuCategoriesRequired'));
        return;
      }

      const payload = {
        ...restaurantData,
        menu_categories: menuCategories,
      };

      if (restaurant) {
        await base44.entities.Restaurant.update(restaurant.id, payload);
      } else {
        await base44.entities.Restaurant.create(payload);
        await fetchUser();
      }
      queryClient.invalidateQueries({ queryKey: ['restaurant'] });
      setShowRestaurantForm(false);
      navigate(createPageUrl("Dashboard"));
    } catch (error) {
      console.error("Error saving restaurant settings:", error);
      setSaveError(error?.response?.data?.detail || error.message || t('setup.saveError', 'Failed to save settings'));
    }
  };

  const toggleDay = (day) => {
    const days = restaurantData.opening_days.includes(day)
      ? restaurantData.opening_days.filter(d => d !== day)
      : [...restaurantData.opening_days, day];
    setRestaurantData({
      ...restaurantData,
      opening_days: days,
      days_per_week: days.length
    });
  };

  const updateMenuCategory = (index, value) => {
    const nextCategories = [...restaurantData.menu_categories];
    nextCategories[index] = value;
    setRestaurantData({
      ...restaurantData,
      menu_categories: nextCategories,
    });
  };

  const addMenuCategory = () => {
    setRestaurantData({
      ...restaurantData,
      menu_categories: [...restaurantData.menu_categories, ''],
    });
  };

  const removeMenuCategory = (index) => {
    const nextCategories = restaurantData.menu_categories.filter((_, categoryIndex) => categoryIndex !== index);
    setRestaurantData({
      ...restaurantData,
      menu_categories: nextCategories,
    });
  };

  return (
    <Layout>
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-xl">
            <Settings className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-4xl font-bold text-slate-900 mb-3">
            {t("setup.welcome")}
          </h1>
          <p className="text-lg text-slate-600">
            {t("setup.subtitle")}
          </p>
        </div>

        {hasExistingData && (
          <Alert className="mb-8 border-blue-200 bg-blue-50">
            <Sparkles className="w-4 h-4 text-blue-600" />
            <AlertDescription className="text-blue-900">
              {t("setup.existingData")}
            </AlertDescription>
          </Alert>
        )}

        {showRestaurantForm ? (
          <Card className="border-0 shadow-xl mb-8">
            <CardHeader>
              <CardTitle>{t("setup.restaurantSettings")}</CardTitle>
              <CardDescription>
                {t("setup.restaurantSettingsDesc")}
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-0 space-y-6">
              <div>
                <Label htmlFor="restaurant_name">{t("setup.restaurantName")}</Label>
                <Input
                  id="restaurant_name"
                  value={restaurantData.name}
                  onChange={(e) => setRestaurantData({...restaurantData, name: e.target.value})}
                  placeholder={t("setup.restaurantNamePlaceholder")}
                />
              </div>

              <div className="space-y-3">
                <div>
                  <Label className="mb-1 block">{t("setup.menuCategories")}</Label>
                  <p className="text-sm text-slate-500">{t("setup.menuCategoriesDesc")}</p>
                </div>
                <div className="space-y-3">
                  {restaurantData.menu_categories.map((category, index) => (
                    <div key={index} className="flex items-center gap-3">
                      <Input
                        value={category}
                        onChange={(e) => updateMenuCategory(index, e.target.value)}
                        placeholder={t("setup.menuCategoryPlaceholder")}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        onClick={() => removeMenuCategory(index)}
                        disabled={restaurantData.menu_categories.length <= 1}
                        aria-label={t("setup.removeCategory")}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
                <Button type="button" variant="outline" onClick={addMenuCategory}>
                  <Plus className="w-4 h-4 mr-2" />
                  {t("setup.addCategory")}
                </Button>
              </div>

              <div>
                <Label className="mb-3 block">{t("setup.openingDays")}</Label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {daysOfWeek.map(day => (
                    <div key={day} className="flex items-center space-x-2">
                      <Checkbox
                        id={day}
                        checked={restaurantData.opening_days.includes(day)}
                        onCheckedChange={() => toggleDay(day)}
                      />
                      <Label htmlFor={day} className="text-sm cursor-pointer font-normal">
                        {day}
                      </Label>
                    </div>
                  ))}
                </div>
                <p className="text-sm text-slate-500 mt-2">
                  {restaurantData.days_per_week} {t("setup.daysSelected")}
                </p>
              </div>

              <div className="border-t pt-6">
                <h3 className="font-semibold text-slate-900 mb-4">{t("setup.openingHours")}</h3>
                
                <div className="space-y-4">
                  <div className="flex items-center gap-4">
                    <Checkbox
                      id="lunch"
                      checked={restaurantData.lunch_service}
                      onCheckedChange={(checked) => setRestaurantData({...restaurantData, lunch_service: checked})}
                    />
                    <Label htmlFor="lunch" className="cursor-pointer font-medium">
                      {t("setup.lunchService")}
                    </Label>
                  </div>
                  
                  {restaurantData.lunch_service && (
                    <div className="grid grid-cols-2 gap-4 pl-8">
                      <div>
                        <Label htmlFor="lunch_start" className="text-sm">{t("setup.from")}</Label>
                        <Input
                          id="lunch_start"
                          type="time"
                          value={restaurantData.lunch_start}
                          onChange={(e) => setRestaurantData({...restaurantData, lunch_start: e.target.value})}
                        />
                      </div>
                      <div>
                        <Label htmlFor="lunch_end" className="text-sm">{t("setup.to")}</Label>
                        <Input
                          id="lunch_end"
                          type="time"
                          value={restaurantData.lunch_end}
                          onChange={(e) => setRestaurantData({...restaurantData, lunch_end: e.target.value})}
                        />
                      </div>
                    </div>
                  )}
                </div>

                <div className="space-y-4 mt-4">
                  <div className="flex items-center gap-4">
                    <Checkbox
                      id="dinner"
                      checked={restaurantData.dinner_service}
                      onCheckedChange={(checked) => setRestaurantData({...restaurantData, dinner_service: checked})}
                    />
                    <Label htmlFor="dinner" className="cursor-pointer font-medium">
                      {t("setup.dinnerService")}
                    </Label>
                  </div>
                  
                  {restaurantData.dinner_service && (
                    <div className="grid grid-cols-2 gap-4 pl-8">
                      <div>
                        <Label htmlFor="dinner_start" className="text-sm">{t("setup.from")}</Label>
                        <Input
                          id="dinner_start"
                          type="time"
                          value={restaurantData.dinner_start}
                          onChange={(e) => setRestaurantData({...restaurantData, dinner_start: e.target.value})}
                        />
                      </div>
                      <div>
                        <Label htmlFor="dinner_end" className="text-sm">{t("setup.to")}</Label>
                        <Input
                          id="dinner_end"
                          type="time"
                          value={restaurantData.dinner_end}
                          onChange={(e) => setRestaurantData({...restaurantData, dinner_end: e.target.value})}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {saveError && (
                <Alert className="border-red-200 bg-red-50 text-red-700">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>{saveError}</AlertDescription>
                </Alert>
              )}
              <div className="flex justify-end gap-3 pt-4">
                <Button variant="outline" onClick={() => setShowRestaurantForm(false)}>
                  {t("setup.cancel")}
                </Button>
                <Button onClick={saveRestaurantSettings} className="bg-blue-600 hover:bg-blue-700">
                  {t("setup.saveAndContinue")}
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Restaurant Settings bearbeiten */}
            {restaurant && (
              <Card className="border-0 shadow-md mb-8">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="p-3 bg-blue-100 rounded-xl">
                      <Settings className="w-6 h-6 text-blue-600" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-slate-900 mb-2">{t("setup.restaurantSettings")}</h3>
                      <p className="text-sm text-slate-600 mb-4">
                        {t("setup.adjustOpeningDays")}
                      </p>
                      <Button
                        variant="outline"
                        className="border-blue-200 text-blue-700 hover:bg-blue-50"
                        onClick={() => setShowRestaurantForm(true)}
                      >
                        <Settings className="w-4 h-4 mr-2" />
                        {t("setup.editSettings")}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        )}

        {/* Orderbird Data Import */}
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-4">
            <Upload className="w-5 h-5 text-slate-600" />
            <h2 className="text-lg font-semibold text-slate-900">{t('pos.orderbirdImportTitle')}</h2>
          </div>
          <Card className="border-0 shadow-md">
            <CardContent className="p-6">
              <p className="text-slate-500 text-sm mb-4">{t('pos.orderbirdImportDesc')}</p>

              {importResult && (
                <div className={`mb-4 p-3 rounded-lg text-sm flex items-start justify-between gap-3 ${
                  importResult.ok
                    ? 'bg-green-50 text-green-700 border border-green-200'
                    : 'bg-red-50 text-red-700 border border-red-200'
                }`}>
                  <div className="flex items-center gap-2">
                    {importResult.ok
                      ? <CheckCircle className="w-4 h-4 flex-shrink-0" />
                      : <AlertTriangle className="w-4 h-4 flex-shrink-0" />}
                    <span>{importResult.message}</span>
                  </div>
                  <button onClick={() => setImportResult(null)} className="text-current opacity-60 hover:opacity-100 flex-shrink-0">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              )}

              <Button
                variant="outline"
                className="border-orange-200 text-orange-700 hover:bg-orange-50"
                disabled={importUploading}
                onClick={() => fileInputRef.current?.click()}
              >
                <Upload className="w-4 h-4 mr-2" />
                {importUploading ? t('pos.uploading') : t('pos.orderbirdUploadButton')}
              </Button>

              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.txt"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) handleOrderbirdUpload(file);
                  e.target.value = '';
                }}
              />
            </CardContent>
          </Card>
        </div>

        {/* POS Integrations */}
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-4">
            <Plug className="w-5 h-5 text-slate-600" />
            <h2 className="text-lg font-semibold text-slate-900">{t('pos.pageTitle')}</h2>
          </div>
          <p className="text-slate-500 text-sm mb-4">{t('pos.pageSubtitle')}</p>
          <POSIntegrationsSection />
        </div>

        {/* Info Box */}
        <Card className="border-0 shadow-md bg-slate-50">
          <CardContent className="p-6">
            <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-blue-500" />
              {t("setup.whatDoesCarloDo")}
            </h3>
            <div className="grid md:grid-cols-2 gap-4 text-sm text-slate-600">
              <div>
                <p className="font-medium text-slate-700 mb-1">{t("setup.forecasts")}</p>
                <p>{t("setup.forecastsDesc")}</p>
              </div>
              <div>
                <p className="font-medium text-slate-700 mb-1">{t("setup.inventoryDesc")}</p>
                <p>{t("setup.inventoryDescText")}</p>
              </div>
              <div>
                <p className="font-medium text-slate-700 mb-1">{t("setup.aiAgent")}</p>
                <p>{t("setup.aiAgentDesc")}</p>
              </div>
              <div>
                <p className="font-medium text-slate-700 mb-1">{t("setup.tasksDesc")}</p>
                <p>{t("setup.tasksDescText")}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Quick Links */}
        <div className="mt-8 text-center">
          <p className="text-sm text-slate-500 mb-4">
            {t("setup.canAccessAnytime")}
          </p>
          <Button variant="outline" onClick={() => navigate(createPageUrl("Dashboard"))}>
            {t("setup.goToDashboard")}
          </Button>
        </div>
      </div>
    </Layout>
  );
}
